import { IAdHocTokenReport } from '../../../lib/types/communication.types';

// The two blocking tiers the edit-field highlight renders — mirrors the preview's
// mark[data-issue] treatment (unknown = red, missing-fallback = orange).
export type TokenIssueKind = 'unknown' | 'missing-fallback';

export interface ITokenSegment {
  text: string;
  issue?: TokenIssueKind;
}

// Character span [start, end) of one flagged token occurrence within a string.
export interface ITokenIssueMatch {
  start: number;
  end: number;
  issue: TokenIssueKind;
}

// A delimited variable token: {path} or {path|fallback}, whitespace-tolerant.
const tokenPattern = (): RegExp => /\{\s*([\w.]+)\s*(\|[^}]*)?\}/g;

// Locates each invalid-token occurrence and its character span. A path is
// 'unknown' when the server report lists it; 'missing-fallback' only for an
// occurrence with no |fallback (adding one resolves that tier).
export const findTokenIssues = (
  text: string,
  report?: IAdHocTokenReport
): ITokenIssueMatch[] => {
  if (!text || !report) return [];
  const unknown = new Set(report.unknown);
  const missingFallback = new Set(report.missingFallback);
  if (!unknown.size && !missingFallback.size) return [];

  const re = tokenPattern();
  const matches: ITokenIssueMatch[] = [];
  let match: RegExpExecArray | null = re.exec(text);
  while (match !== null) {
    const [full, path, fallback] = match;
    const issue: TokenIssueKind | undefined = unknown.has(path)
      ? 'unknown'
      : (missingFallback.has(path) && !fallback ? 'missing-fallback' : undefined);
    if (issue) matches.push({ start: match.index, end: match.index + full.length, issue });
    match = re.exec(text);
  }
  return matches;
};

// Splits authored text into consecutive segments, flagging each invalid-token
// occurrence so a highlight backdrop can mark it. Returns a single plain segment
// when there is nothing to flag.
export const segmentTokenIssues = (
  text: string,
  report?: IAdHocTokenReport
): ITokenSegment[] => {
  if (!text) return [];
  const matches = findTokenIssues(text, report);
  if (!matches.length) return [{ text }];

  const segments: ITokenSegment[] = [];
  let lastIndex = 0;
  matches.forEach((mt) => {
    if (mt.start > lastIndex) segments.push({ text: text.slice(lastIndex, mt.start) });
    segments.push({ text: text.slice(mt.start, mt.end), issue: mt.issue });
    lastIndex = mt.end;
  });
  if (lastIndex < text.length) segments.push({ text: text.slice(lastIndex) });
  return segments;
};
